import classNames from "classnames";
import { MAX_SCHEDULE_MILESTONE_OFFSET } from "@common/constants/domains/accounting/schedules";
import { ScheduleMilestoneAnchor } from "@common/types/domains/accounting/schedule";
import { Button } from "@design-system/Button/Button";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { HStack } from "@design-system/HStack/HStack";
import { Input } from "@design-system/Input/Input";
import { NumberFormat } from "@design-system/NumberFormat/NumberFormat";
import { Picker } from "@design-system/Picker/Picker.lib";
import { CloseIcon } from "@icons";
import {
    ScheduleMilestoneForm,
    toMilestoneAnchorPatch,
    toMilestoneOffsetPatch,
} from "./scheduleMilestoneFormHelpers";
import {
    MilestoneRowErrors,
    pickMilestoneRowErrorMessage,
} from "./scheduleMilestoneFormErrors";
import { ScheduleNumericInput } from "./ScheduleNumericInput";
import { SchedulePercentInput } from "./SchedulePercentInput";
import {
    MILESTONE_ANCHOR_ITEMS,
    MILESTONE_OFFSET_UNIT_ITEMS,
    MILESTONE_TIMING_ITEMS,
} from "./scheduleRecognitionUi";
import styles from "./ScheduleDrawer.module.scss";

interface ScheduleMilestoneRowProps {
    index: number;
    milestone: ScheduleMilestoneForm;
    /** The last row takes the remainder, so its percent is shown but not typed. */
    isAllocationLocked: boolean;
    isReadOnly: boolean;
    errors?: MilestoneRowErrors;
    onChange: (patch: Partial<ScheduleMilestoneForm>) => void;
    onAllocationChange: (allocationPercent: number) => void;
    /** Omitted on the only row. */
    onRemove?: () => void;
}

/** One milestone as a sentence: number, percent, description, timing, anchor. */
export const ScheduleMilestoneRow = ({
    index,
    milestone,
    isAllocationLocked,
    isReadOnly,
    errors,
    onChange,
    onAllocationChange,
    onRemove,
}: ScheduleMilestoneRowProps) => {
    const number = index + 1;
    const error = pickMilestoneRowErrorMessage(errors);
    const hasOffset = milestone.offsetUnit !== null;
    const isPercentReadOnly = isReadOnly || isAllocationLocked;
    return (
        <div className={styles.milestoneRow}>
            <HStack gap={12} align="center" wrap>
                <span className={styles.milestoneIndex}>{number}</span>
                <SchedulePercentInput
                    value={milestone.allocationPercent}
                    isLocked={isPercentReadOnly}
                    onChange={onAllocationChange}
                    aria-label={`Milestone ${number} percent`}
                />
                <Input
                    size="md"
                    aria-label={`Milestone ${number} description`}
                    placeholder="Description"
                    value={milestone.name}
                    readOnly={isReadOnly}
                    className={classNames(
                        styles.drawerInput,
                        styles.milestoneDescriptionInput,
                    )}
                    onChange={(event) => onChange({ name: event.target.value })}
                />
                <Picker.Select
                    items={MILESTONE_TIMING_ITEMS}
                    value={hasOffset ? "after" : "on"}
                    disabled={isReadOnly}
                    onValueChange={(kind) => {
                        if (kind == null) {
                            return;
                        }
                        onChange(
                            toMilestoneOffsetPatch({
                                milestone,
                                hasOffset: kind === "after",
                            }),
                        );
                    }}
                    trigger={
                        <Picker.Button
                            variant="primary"
                            size="md"
                            aria-label={`Milestone ${number} timing`}
                        />
                    }
                />
                {hasOffset && (
                    <>
                        <NumberFormat
                            value={milestone.offsetCount ?? ""}
                            onValueChange={(numberValues, sourceInfo) => {
                                if (sourceInfo.event == null) {
                                    return;
                                }
                                onChange({
                                    offsetCount:
                                        numberValues.floatValue ?? null,
                                });
                            }}
                            decimalScale={0}
                            allowNegative={false}
                            isAllowed={({ floatValue }) =>
                                floatValue === undefined ||
                                floatValue <= MAX_SCHEDULE_MILESTONE_OFFSET
                            }
                            readOnly={isReadOnly}
                            aria-label={`Milestone ${number} count`}
                            customInput={ScheduleNumericInput}
                            className={classNames(
                                styles.drawerInput,
                                styles.numericInput,
                                styles.milestoneCountInput,
                            )}
                        />
                        <Picker.Select
                            items={MILESTONE_OFFSET_UNIT_ITEMS}
                            value={milestone.offsetUnit}
                            disabled={isReadOnly}
                            onValueChange={(offsetUnit) => {
                                if (offsetUnit == null) {
                                    return;
                                }
                                onChange({ offsetUnit });
                            }}
                            trigger={
                                <Picker.Button
                                    variant="primary"
                                    size="md"
                                    aria-label={`Milestone ${number} unit`}
                                />
                            }
                        />
                    </>
                )}
                <Picker.Select
                    items={MILESTONE_ANCHOR_ITEMS}
                    value={milestone.anchor}
                    disabled={isReadOnly}
                    onValueChange={(anchor) => {
                        if (anchor == null) {
                            return;
                        }
                        onChange(toMilestoneAnchorPatch({ milestone, anchor }));
                    }}
                    trigger={
                        <Picker.Button
                            variant="primary"
                            size="md"
                            aria-label={`Milestone ${number} anchor`}
                        />
                    }
                />
                {milestone.anchor === ScheduleMilestoneAnchor.specificDate && (
                    <DatePicker
                        mode="day"
                        variant="button"
                        placeholder="Select date"
                        value={milestone.specificDate ?? undefined}
                        disabled={isReadOnly}
                        onValueChange={(date) =>
                            onChange({ specificDate: date ?? null })
                        }
                        fieldControlProps={{
                            "aria-invalid": errors?.specificDate
                                ? true
                                : undefined,
                        }}
                        buttonProps={{
                            size: "md",
                            variant: "primary",
                            "aria-label": `Milestone ${number} date`,
                        }}
                    />
                )}
                {onRemove && !isReadOnly && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="md"
                        iconOnly
                        className={styles.milestoneRemove}
                        aria-label={`Remove milestone ${number}`}
                        onClick={onRemove}
                    >
                        <CloseIcon />
                    </Button>
                )}
            </HStack>
            {error && <p className={styles.fieldError}>{error}</p>}
        </div>
    );
};
